import types from '../mutation-types';
import CustomPromptAPI from '../../api/customPrompt';
import CustomPromptTemplateAPI from '../../api/customPromptTemplate';

export const state = {
  prompt: {
    id: null,
    content: '',
    active: false,
    max_length: 2000,
    has_api_key: true,
  },
  templates: [],
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isValidating: false,
    isFetchingTemplates: false,
  },
  validation: {
    valid: null,
    message: '',
  },
};

export const getters = {
  getPrompt(_state) {
    return _state.prompt;
  },
  getTemplates(_state) {
    return _state.templates;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getValidation(_state) {
    return _state.validation;
  },
};

export const actions = {
  fetchPrompt: async ({ commit }) => {
    commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isFetching: true });
    try {
      const response = await CustomPromptAPI.show();
      commit(types.SET_CUSTOM_PROMPT, response.data);
    } catch (error) {
      // Prompt may not exist yet, which is fine
      if (error?.response?.status !== 404) {
        throw error;
      }
    } finally {
      commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isFetching: false });
    }
  },

  savePrompt: async ({ commit, state: _state }, promptData) => {
    commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isSaving: true });
    try {
      let response;
      if (_state.prompt.id) {
        response = await CustomPromptAPI.update({
          custom_prompt: promptData,
        });
      } else {
        response = await CustomPromptAPI.create({
          custom_prompt: promptData,
        });
      }
      commit(types.SET_CUSTOM_PROMPT, response.data);
      return response.data;
    } finally {
      commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isSaving: false });
    }
  },

  deletePrompt: async ({ commit, state: _state }) => {
    commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isSaving: true });
    try {
      await CustomPromptAPI.delete();
      commit(types.SET_CUSTOM_PROMPT, {
        id: null,
        content: '',
        active: false,
        max_length: _state.prompt.max_length,
      });
    } finally {
      commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isSaving: false });
    }
  },

  validatePrompt: async ({ commit }, content) => {
    commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isValidating: true });
    commit(types.SET_CUSTOM_PROMPT_VALIDATION, { valid: null, message: '' });
    try {
      const response = await CustomPromptAPI.validatePrompt(content);
      commit(types.SET_CUSTOM_PROMPT_VALIDATION, {
        valid: true,
        message: response.data.message,
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        'Validation failed. Please try again.';
      commit(types.SET_CUSTOM_PROMPT_VALIDATION, {
        valid: false,
        message,
      });
      throw error;
    } finally {
      commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isValidating: false });
    }
  },

  fetchTemplates: async ({ commit }) => {
    commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isFetchingTemplates: true });
    try {
      const response = await CustomPromptTemplateAPI.get();
      commit(types.SET_CUSTOM_PROMPT_TEMPLATES, response.data);
    } catch (error) {
      // silently fail
    } finally {
      commit(types.SET_CUSTOM_PROMPT_UI_FLAG, { isFetchingTemplates: false });
    }
  },

  clearValidation: ({ commit }) => {
    commit(types.SET_CUSTOM_PROMPT_VALIDATION, { valid: null, message: '' });
  },
};

export const mutations = {
  [types.SET_CUSTOM_PROMPT_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_CUSTOM_PROMPT](_state, data) {
    _state.prompt = { ..._state.prompt, ...data };
  },
  [types.SET_CUSTOM_PROMPT_TEMPLATES](_state, data) {
    _state.templates = data;
  },
  [types.SET_CUSTOM_PROMPT_VALIDATION](_state, data) {
    _state.validation = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
